<template>
  <div class="program-dashboard">
    <!-- Error Message -->
    <b-alert
      v-if="errorMessage"
      show
      variant="danger"
      class="text-center rounded-4"
    >
      <h4 class="alert-heading mb-3">Akses Ditolak</h4>
      <p>
        Anda tidak memiliki akses ke halaman ini. Silakan klik di bawah untuk
        logout.
      </p>
      <b-button
        href="/logout"
        variant="outline-danger"
        class="mt-2 rounded-pill px-4"
      >
        <i class="uil uil-sign-out-alt me-2"></i>
        {{ $t("navbar.dropdown.marcus.list.logout") }}
      </b-button>
    </b-alert>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="d-flex justify-content-center align-items-center min-vh-75"
    >
      <loading-vue></loading-vue>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Empty State -->
      <b-row
        v-if="setPaketSoal.length === 0 && !errorMessage"
        class="min-vh-75"
      >
        <b-col class="text-center my-auto">
          <div class="empty-state p-5">
            <i class="fas fa-books fs-1 text-muted mb-4"></i>
            <h4>Tidak Ada Program yang Tersedia</h4>
            <p class="text-muted">
              Tidak ada pertanyaan atau program yang Anda pilih tidak tersedia.
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Program Grid -->
      <b-row class="g-4">
        <b-col
          v-for="program in setPaketSoal"
          :key="program.id"
          sm="6"
          md="4"
          xl="3"
        >
          <template v-if="program.paket">
            <div
              class="modern-card"
              :class="{ disabled: program.length_soal === 0 }"
            >
              <!-- Card Header -->
              <div class="card-banner">
                <div class="banner-background"></div>
                <div class="banner-content">
                  <div class="program-badges">
                    <span
                      class="badge-pill"
                      :class="{ 'is-exam': program.soal.is_ujian }"
                    >
                      {{ program.soal.is_ujian ? "Ujian" : "Latihan" }}
                    </span>

                    <div
                      class="program-status"
                      v-if="program.is_answered || program.is_finished"
                    >
                      <i class="fas fa-check-circle"></i>
                      <span>Selesai</span>
                    </div>
                  </div>
                  <h3
                    class="program-type"
                    v-html="formattedText(program.paket)"
                  ></h3>
                </div>
              </div>

              <!-- Card Body -->
              <div class="card-content">
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <h5 class="program-name">{{ program.soal.name || "-" }}</h5>

                  <span class="program-id">
                    <i class="fas fa-bookmark"></i>
                    <h6>{{ program.id_soal || "-" }}</h6>
                  </span>
                </div>

                <div class="program-stats">
                  <div class="stat-item">
                    <i class="fas fa-file-alt"></i>
                    <span>{{ program.length_soal || 0 }} Soal</span>
                  </div>
                  <div class="stat-item">
                    <i class="fas fa-clock"></i>
                    <span>{{ program.waktu || 0 }} Menit</span>
                  </div>
                </div>

                <a
                  :href="getActionUrl(program)"
                  :class="[
                    'action-button',
                    program.is_answered || program.is_finished
                      ? 'completed'
                      : '',
                    program.length_soal === 0 ? 'disabled' : '',
                  ]"
                >
                  <span>{{ getActionText(program) }}</span>
                  <i class="fas fa-arrow-right"></i>
                </a>
              </div>
            </div>
          </template>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import appConfig from "@/app.config";
import axios from "axios";
import loadingVue from "@/components/loading.vue";

export default {
  components: {
    loadingVue,
  },
  page: {
    title: "Student",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "CBT - Siswa",
      items: [
        { text: "Dashboard", href: "/" },
        { text: "Student", active: true },
      ],
      params: this.$route.params,
      isLoading: true,
      setPaketSoal: [],
      errorMessage: false,
    };
  },
  mounted() {
    this.getPaketSoal();
  },
  methods: {
    formattedText(text) {
      if (!text) return "";
      return text
        .replace(" ", "<br>")
        .replace("Test", "")
        .trim();
    },
    getActionUrl(program) {
      return !program.is_answered && !program.is_finished
        ? "/student/ujian/detail/" + program.id_soal
        : "/scores";
    },
    getActionText(program) {
      if (program.length_soal === 0) return "No Questions";
      return !program.is_answered && !program.is_finished
        ? "Mulai Ujian"
        : "Lihat Nilai";
    },
    async getPaketSoal() {
      let url = "";
      if (this.params.subid && this.params.programname !== "latihan") {
        url = `/api/json/get-distribusi-soal?is_ujian=1&programme_ids=${this.params.subid}&question_category=${this.params.questioncategory}`;
      } else if (this.params.programname == "semua-soal") {
        url = `/api/json/get-distribusi-soal`;
      } else if (
        this.params.programname == "latihan" &&
        this.params.subname == "kecermatan"
      ) {
        url = `/api/json/get-distribusi-soal-latihan-kecermatan?is_ujian=0&tag_order=1&paket=${this.params.subname}`;
      } else if (this.params.programname == "latihan") {
        url = `/api/json/get-distribusi-soal?is_ujian=0&paket=${this.params.subname}`;
      } else {
        url = `/api/json/get-distribusi-soal?is_ujian=1&programme_ids=${this.params.id}`;
      }

      try {
        const response = await axios.get(
          process.env.VUE_APP_API_BASE_URL + url
        );
        this.setPaketSoal = response.data;
      } catch (error) {
        if (error.response.status === 403) {
          this.errorMessage = true;
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
  middleware: "authentication",
};
</script>

<style scoped>
/* Base Card Styling */
.modern-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  border: 1px solid #eef2f7;
  height: 100%;
}

.modern-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
}

.modern-card.disabled {
  opacity: 0.7;
  pointer-events: none;
}

/* Enhanced Banner Styling */
.card-banner {
  position: relative;
  padding: 1rem;
  min-height: 140px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.banner-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #6433a4 0%, #9166cc 100%);
  opacity: 0.95;
  z-index: 1;
}

.banner-background::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 50%
  );
  animation: rotate 30s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.banner-content {
  position: relative;
  z-index: 2;
}

.banner-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.program-badges {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
}

.badge-pill {
  padding: 0.2rem 1rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.badge-pill.is-exam {
  background: rgba(255, 207, 51, 0.2);
  color: #ffe169;
  border-color: rgba(255, 207, 51, 0.3);
}

.program-id {
  /* padding: 0.1rem 1rem; */
  /* border-radius: 50px; */
  font-size: 1rem;
  font-weight: 500;
  /* background: rgba(0, 0, 0, 0.2); */
  color: rgba(0, 0, 0, 0.2);
  /* color: rgba(255, 255, 255, 0.9); */
  display: flex;
  gap: 0.5rem;
}

.program-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.2rem 1rem;
  background: rgba(52, 211, 153, 0.2);
  color: #4ade80;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(52, 211, 153, 0.3);
}

.program-type {
  color: white;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Card Content Styling */
.card-content {
  padding: 1rem;
}

.program-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}

/* Program Stats */
.program-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #64748b;
}

.stat-item i {
  color: #6433a4;
}

/* Action Button */
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: #6433a4;
  color: white;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
}

.action-button:hover {
  background: #522b87;
  color: white;
  text-decoration: none;
}

.action-button.completed {
  background: #e2e8f0;
  color: #64748b;
}

.action-button.completed:hover {
  background: #cbd5e1;
}

.action-button.disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}

/* Status Badge */
.status-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #22c55e;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);
}

/* Empty State */
.empty-state {
  background: #f8fafc;
  border-radius: 16px;
  padding: 3rem;
}

/* Hover Effects */
.modern-card:hover .badge-pill {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modern-card:hover .banner-background {
  transform: scale(1.05);
  transition: transform 0.3s ease;
}

/* Loading State */
.min-vh-75 {
  min-height: 75vh;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .program-dashboard {
    padding: 1rem 0;
  }

  .card-banner {
    min-height: 120px;
    padding: 1.25rem;
  }

  .program-type {
    font-size: 1.2rem;
  }

  .banner-header {
    flex-direction: column;
    gap: 0.75rem;
  }

  .program-badges {
    width: 100%;
    justify-content: space-between;
  }

  .card-content {
    padding: 1.25rem;
  }

  .program-name {
    font-size: 1rem;
  }

  .program-stats {
    padding: 0.5rem;
    margin-bottom: 1.25rem;
  }

  .action-button {
    padding: 0.6rem 1.25rem;
  }
}

/* Small Screen Adjustments */
@media (max-width: 576px) {
  .program-type {
    font-size: 1.1rem;
  }

  .badge-pill,
  .program-id {
    font-size: 0.8rem;
    padding: 0.35rem 0.85rem;
  }

  .program-stats {
    gap: 0.5rem;
  }
}
</style>
